/* eslint-disable padding-line-between-statements */
/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ApolloProvider } from 'react-apollo';
import { ApolloProvider as ApolloHooksProvider } from '@apollo/react-hooks';
import { ThemeProvider } from 'styled-components';
import {
  GlobalStyleWrap, theme,
} from '@amount/frontend-components';
import Cookies from 'universal-cookie';
import * as Sentry from '@sentry/browser';

import './analytics';
import './index.css';
import App from './App';
import SessionManager, { BootstrapResult } from './util/sessionManager';
import Boundary from './components/Common/Error/Boundary';
import { Reconnecting } from './_shared/ThemeTwo/Session/Reconnecting';
import { client } from './apolloClient';
import { FeatureFlagWrapper } from './util/featureFlag';
import { mergeWithCopyArrays } from '../config/utils';
import { ITenantTheme } from './customTypings/tenantTheme';
import { TenantTheme } from './config/tenantTheme';

const combinedTheme: ITenantTheme = mergeWithCopyArrays(
  theme, { ...TenantTheme }
);

const Index: React.FC = () => (
  <Boundary>
    <ThemeProvider theme={combinedTheme}>
      <>
        <GlobalStyleWrap {...combinedTheme} />
        {/* Need both providers until we can update react-apollo to ^3.0.0 */}
        <ApolloProvider client={client}>
          <ApolloHooksProvider client={client}>
            <FeatureFlagWrapper>
                <App />
            </FeatureFlagWrapper>
          </ApolloHooksProvider>
        </ApolloProvider>
      </>
    </ThemeProvider>
  </Boundary>
);

const cookies = new Cookies();

const renderApp = () => {
  ReactDOM.render(
    <Index />,
    document.getElementById('root') as HTMLElement
  );
};

// GE-3271: render the transient "Reconnecting your session…" interstitial on the
// recoverable re-auth path, so the happy-path expiry shows reassuring copy
// (not a credential prompt) during the brief silent handoff redirect. Wrapped in
// the theme providers only — no Apollo/Router needed — to keep it fast and avoid
// a flash of unstyled content.
const renderReconnecting = () => {
  ReactDOM.render(
    <ThemeProvider theme={combinedTheme}>
      <>
        <GlobalStyleWrap {...combinedTheme} />
        <Reconnecting />
      </>
    </ThemeProvider>,
    document.getElementById('root') as HTMLElement
  );
};

// Option 2b: the token is no longer accepted from the URL (?jwtToken=) or read
// from a JS-readable cookie. If we don't already have a session (localStorage),
// bootstrap it from the HttpOnly customer_dash_token cookie via a credentialed
// request to the backend, then render. This is safe when the backend flag is
// off (endpoint 404s -> no token -> normal unauthenticated flow).
//
// GE-3271: if the backend rejects a stale cookie it returns
// `401 { reauthenticate: true }` (and has already deleted the cookie). We then
// branch on whether the failure is recoverable:
//   - Recoverable (BootstrapResult.Reauthenticate: token_expired / no_token) —
//     the avant-basic web session is still valid, so we route the user through
//     the login/handoff which silently re-mints a fresh cookie. This is the
//     happy path; no "please log in" prompt is shown. We MUST NOT retry the
//     bootstrap with the same dead cookie — that unchanged-cookie retry is what
//     produced the UAT4 redirect loop. A sessionStorage-backed safety cap
//     prevents a tight loop if an environment is misconfigured.
//   - Non-recoverable (BootstrapResult.ReauthenticateFailed:
//     authentication_failed + invalidated|unknown|malformed|failed) — the
//     underlying session cannot mint a new token, so we send the user to the
//     /loggedOut route which surfaces the genuine "please log in" popup instead
//     of bouncing through the handoff.
const bootstrapAndRender = async () => {
  if (!SessionManager.hasSession) {
    const result = await SessionManager.bootstrapTokenFromCookie();

    if (result === BootstrapResult.Reauthenticate) {
      if (SessionManager.canAttemptReauth()) {
        // Navigate to login/handoff and stop. Do NOT retry the bootstrap.
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'bootstrapAndRender: reauthenticate (recoverable) -> redirecting to handoff',
          level: Sentry.Severity.Info,
        });
        // Show a reassuring "reconnecting" interstitial during the brief silent
        // redirect rather than a blank page or a scary "please log in" prompt.
        // Guarded: the redirect is the critical action and MUST NOT be blocked
        // by a failure to render the interstitial. Previously an exception here
        // (e.g. the config-serialized `bodyText` object breaking `.map()`) threw
        // before redirectToReauth() ran, stranding the user on a blank page with
        // no navigation and no safety-cap increment (GE-3271).
        try {
          renderReconnecting();
        } catch (e) {
          Sentry.captureException(e);
        }
        SessionManager.redirectToReauth();

        return;
      }

      // Safety cap hit: this is the misconfiguration/loop guard GE-3271 added.
      // Capture (not just breadcrumb) so it can alert, then render normally.
      Sentry.captureMessage(
        'bootstrapAndRender: re-auth safety cap reached, rendering unauthenticated',
        Sentry.Severity.Warning
      );
    }

    if (result === BootstrapResult.ReauthenticateFailed) {
      // Non-recoverable: a silent re-mint will not work (the web session is
      // gone). Route to the /loggedOut popup which prompts a genuine login,
      // rather than looping the user through the handoff.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'bootstrapAndRender: reauthenticate failed (non-recoverable) -> routing to loggedOut',
        level: Sentry.Severity.Info,
      });
      window.location.replace(
        `${process.env.PUBLIC_URL || ''}${SessionManager.urlRouter.loggedOut}`
      );

      return;
    }

    if (result === BootstrapResult.Success) {
      // Reset the safety cap so a later expiry in this tab can re-auth again.
      SessionManager.clearReauthAttempts();
    }
    // On Unavailable, or Reauthenticate past the safety cap, fall through to a
    // normal render rather than bouncing the user through the handoff again.
  }

  const redirectPage = cookies.get('redirectAfterAuth');
  if (redirectPage) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'bootstrapAndRender: consuming redirectAfterAuth, returning user to saved location',
      level: Sentry.Severity.Info,
    });
    cookies.remove('redirectAfterAuth');
    window.location.replace(redirectPage);

    return;
  }

  renderApp();
};

void bootstrapAndRender();

/* eslint-disable @typescript-eslint/no-explicit-any */
if ((module as any).hot) {
  (module as any).hot.accept('./App', () => {
    renderApp();
  });
}
/* eslint-enable */
// investigate re-enabling:
// https://github.com/facebook/create-react-app/pull/3817
// https://github.com/facebook/create-react-app/issues/2554
// registerServiceWorker();
// unregisterServiceWorker();
